import { type ColumnDefinition } from '@turnpike/portal-data-grid';
import { useMemo } from 'react';

import { formatEndTime, formatStartTime, parseDateTimeOffset } from '../model/utils/time';

import { Assignee } from './components/Assignee';
import { Organization } from './components/Organization';
import { Status } from './components/Status';
import { Time } from './components/Time';
import { Title } from './components/Title';

import type { TaskModel } from '../model/types/task.model';
import type { FilterStatusType } from '@/app/types/TaskStatusFilter';
import type { Organization as OrganizationModel } from '@/features/Shared/model/types/organization.dto';

interface Props {
	filter: FilterStatusType;
	organizations: Record<string, OrganizationModel> | undefined;
}

export const useTableLayoutColumns = ({ filter, organizations }: Props) => {
	const columns = useMemo((): ColumnDefinition<TaskModel>[] => {
		return [
			{
				key: 'title',
				title: 'Title',
				width: 220,
				draggable: false,
				render: (_value, row) => <Title displayStatus={filter === 'all'} item={row} />,
				sortFn: (a, b) => a.title.localeCompare(b.title),
			},
			{
				key: 'organization',
				title: 'Organization',
				width: 150,
				render: (_value, row) => (
					<Organization item={organizations?.[row.organizationId]} />
				),
			},
			{
				key: 'status',
				title: 'status',
				width: 150,
				align: 'center',
				render: (_value, row) => <Status item={row} />,
			},

			{
				key: 'assignee',
				title: 'Assignee',
				width: 120,
				render: () => <Assignee />,
			},
			{
				key: 'start',
				title: 'start',
				width: 200,
				sortFn: (a, b) =>
					(parseDateTimeOffset(a.start)?.toMillis() ?? 0) -
					(parseDateTimeOffset(b.start)?.toMillis() ?? 0),
				render: (_value, row) => (
					<Time time={row.start} status={row.status} formatDateTime={formatStartTime} />
				),
			},
			{
				key: 'due',
				title: 'due',
				width: 200,
				sortFn: (a, b) =>
					(parseDateTimeOffset(a.end)?.toMillis() ?? 0) -
					(parseDateTimeOffset(b.end)?.toMillis() ?? 0),
				render: (_value, row) => (
					<Time time={row.end} status={row.status} formatDateTime={formatEndTime} />
				),
			},
		];
	}, [filter, organizations]);

	return { columns };
};
